<template>
    <div id="inner-auth-content" class="d-flex flex-column align-center justify-left">
        <div class="d-flex align-center mt-8">
            <span class="d-flex justify-center responsive-auth-font" style="color: darkgrey; width: 500px"> {{ $t('auth.reset.intro') }} </span>
        </div>

        <v-form class="mt-9" style="width: 300px" ref="form" lazy-validation>
            <v-text-field v-model="form.email" :rules="[rules.required, rules.email]" type="text" :label="$t('auth.reset.emailConfirmation')" autofocus dense class="mb-3 left-input" data-cy="email-confirmation"></v-text-field>

            <p v-if="form.errors && form.errors.email" class="red--text subtitle-2 mb-0" data-cy="error-email">
                {{ tServer(String(form.errors.email)) }}
            </p>

            <v-text-field
                v-model="form.password"
                :rules="[rules.required, rules.passwordStrength]"
                :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                @input="validatePasswordStrength"
                :label="$t('auth.password')"
                autocomplete="current-password"
                dense
                class="mb-3 left-input"
                data-cy="password"
            ></v-text-field>

            <p v-if="form.errors && form.errors.password" class="red--text subtitle-2 mb-0" data-cy="error-password">
                {{ tServer(String(form.errors.password)) }}
            </p>

            <v-text-field
                v-model="form.password_confirmation"
                :rules="[rules.required, rules.confirmPassword]"
                :type="showPassword ? 'text' : 'password'"
                @keyup.enter="submitForm"
                :label="$t('userDetails.passwordConfirmation')"
                autocomplete="current-password"
                dense
                class="mb-3 left-input"
                data-cy="password-confirmation"
            ></v-text-field>

            <!-- Password Strength Indicator -->
            <div v-if="form.password || form.password_confirmation" class="password-requirements mb-4">
                <div class="requirement-item" :class="{ valid: passwordValidation.minLength }">
                    <v-icon small :color="passwordValidation.minLength ? 'success' : 'grey'">
                        {{ passwordValidation.minLength ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="ml-2">{{ $t('userDetails.req.minLength') }}</span>
                </div>
                <div class="requirement-item" :class="{ valid: passwordValidation.hasUpperCase && passwordValidation.hasLowerCase }">
                    <v-icon small :color="passwordValidation.hasUpperCase && passwordValidation.hasLowerCase ? 'success' : 'grey'">
                        {{ passwordValidation.hasUpperCase ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="ml-2">{{ $t('userDetails.req.letterCase') }}</span>
                </div>
                <div class="requirement-item" :class="{ valid: passwordValidation.hasNumber && passwordValidation.hasSpecialChar }">
                    <v-icon small :color="passwordValidation.hasNumber && passwordValidation.hasSpecialChar? 'success' : 'grey'">
                        {{ passwordValidation.hasNumber && passwordValidation.hasSpecialChar ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="ml-2">{{ $t('userDetails.req.numberSpecial') }}</span>
                </div>
                <div class="requirement-item" :class="{ valid: form.password_confirmation===form.password  }">
                    <v-icon small :color="form.password_confirmation===form.password  && form.password!=null && form.password_confirmation!=null? 'success' : 'grey'">
                        {{ form.password_confirmation===form.password ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                    </v-icon>
                    <span class="ml-2">{{ $t('userDetails.req.match') }}</span>
                </div>
            </div>

            <!--<p v-if="form.errors && form.errors.password_confirmation" class="red--text subtitle-2 mb-0" data-cy="error-password-confirmation">
                {{ tServer(String(form.errors.password_confirmation)) }}
            </p>-->

            <p v-if="form.errors && form.errors.message" class="red--text subtitle-2 mb-0" dusk="error-message" data-cy="error-message">
                {{ tServer(String(form.errors.message)) }}
            </p>

            <div class="d-flex justify-center">
                <v-btn @click="submitForm" :loading="loading" class="mt-2 myprovet-btn-ok" data-cy="submit-button"> {{ $t('auth.reset.submit') }} </v-btn>
            </div>
        </v-form>

        <div class="mt-4 mb-8">
            <span class="d-flex mt-1 justify-left responsive-auth-font" style="color: darkgrey">
                {{ $t('auth.newHere') }}

                <inertia-link class="no-underline" href="/register"> &nbsp;{{ $t('auth.registerHere') }} </inertia-link>
            </span>
        </div>

        <social-media-auth> </social-media-auth>
    </div>
</template>

<script>
import AuthLayout from '../Layouts/AuthLayout';
import { i18n } from '@/i18n';

export default {
    props: {
        success: String,
    },

    data() {
        return {
            form: {
                email: null,
                password: null,
                password_confirmation: null,
                errors: null,
            },
            passwordValidation: {
                minLength: false,
                hasUpperCase: false,
                hasLowerCase: false,
                hasNumber: false,
                hasSpecialChar: false,
            },
            showPassword: false,
            loading: false,
        };
    },
    computed: {
        rules() {
            return {
                required: (value) => (value && !!value) || this.$t('validation.required'),
                confirmPassword: (value) => this.validateConfirmPassword(value) || this.$t('auth.reset.confirmPassword'),
                passwordStrength: (value) => this.isPasswordStrong(value) || this.$t('userDetails.rules.passwordWeak'),
                email: function (value) {
                    var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return value && re.test(value) ? true : i18n.t('createUser.invalidEmail');
                },
            };
        }
    },
    methods: {
        validatePasswordStrength() {
            const password = this.form.password || '';

            this.passwordValidation = {
                minLength: password.length >= 8,
                hasUpperCase: /[A-Z]/.test(password),
                hasLowerCase: /[a-z]/.test(password),
                hasNumber: /[0-9]/.test(password),
                hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
            };
        },

        isPasswordStrong(password) {
            if (!password) return false;

            const minLength = password.length >= 8;
            const hasUpperCase = /[A-Z]/.test(password);
            const hasLowerCase = /[a-z]/.test(password);
            const hasNumber = /[0-9]/.test(password);
            const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

            return minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
        },

        stripQueryParams() {
            let url = new String(window.location);

            let end = url.lastIndexOf('#');
            if (end >= 0) {
                url = url.substring(0, end);
            }

            end = url.lastIndexOf('?');
            if (end >= 0) {
                url = url.substring(0, end);
            }

            return url;
        },

        getURLToken() {
            let urlSegments = this.stripQueryParams()
                .split('/')
                .filter((e) => e) // remove empty parts
                .slice(-1);

            return urlSegments.length > 0 ? urlSegments[0] : null;
        },

        validateConfirmPassword(value) {
            return value === this.form.password;
        },

        submitForm() {
            if (!this.$refs.form.validate()) return;

            this.loading = true;

            this.form.token = this.getURLToken();

            this.$inertia.post('/reset-password', this.form, {
                onSuccess: () => {
                    this.loading = false;
                    this.form.errors = null;

                    this.showSuccess(this.$t('auth.reset.done'));
                },
                onError: (errors) => {
                    this.loading = false;
                    this.form.errors = errors;
                },
            });
        },
    },

    layout: AuthLayout,
};
</script>

<style scoped>
.no-underline {
    text-decoration: none;
}

.password-requirements {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 12px;
}

.requirement-item {
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #666;
    margin-bottom: 6px;
    transition: color 0.3s ease;
}

.requirement-item:last-child {
    margin-bottom: 0;
}

.requirement-item.valid {
    color: #4caf50;
}

@media only screen and (min-width: 1025px) {
    #inner-auth-content {
        max-width: 350px;
    }
}
</style>
